import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { Country, Currency } from '../../models';
import { environment } from '@environment';
import { CURRENCIES_FIELDS, CURRENCIES_JSON_ASSET } from '../../constants';

/**
 * Service for fetching and caching currency list data from remote API or fallback JSON asset.
 */
@Injectable({
  providedIn: 'root',
})
export class CurrenciesService {
  currencies: Currency[] = [];
  endpoint: string = environment.apiCurrenciesUrl + CURRENCIES_FIELDS;
  currencies$: BehaviorSubject<Currency[]> = new BehaviorSubject<Currency[]>(
    [],
  );

  /**
   * Initializes the CurrenciesService.
   * @param httpClient - Angular HTTP client for making API requests.
   */
  constructor(private httpClient: HttpClient) {}

  /**
   * The `getCurrencies` function retrieves currency information from a list of countries
   */
  getCurrencies() {
    this.httpClient.get<Country[]>(this.endpoint).subscribe({
      next: (countries) => {
        const currenciesMap = new Map<
          string,
          { code: string; name: string; symbol: string }
        >();

        countries.forEach((country) => {
          if (country.currencies) {
            Object.keys(country.currencies).forEach((currencyCode) => {
              const currency = country.currencies[currencyCode];

              if (!currenciesMap.has(currencyCode)) {
                this.currencies = [
                  ...this.currencies,
                  {
                    code: currencyCode,
                    name: currency.name,
                    symbol: currency.symbol,
                  },
                ];

                currenciesMap.set(currencyCode, {
                  code: currencyCode,
                  name: currency.name,
                  symbol: currency.symbol,
                });
              }
            });
          }
        });

        this.currencies.sort((a, b) => {
          const codeA = a.code || '';
          const codeB = b.code || '';
          return codeA.localeCompare(codeB);
        });
        this.currencies$.next(this.currencies);
      },
      error: (_error) => {
        this.getCurrenciesLocal();
      },
    });
  }

  /**
   * The function `getCurrenciesLocal` fetches currency data from a JSON asset.
   */
  getCurrenciesLocal() {
    this.httpClient.get<Currency[]>(CURRENCIES_JSON_ASSET).subscribe({
      next: (currencies) => {
        this.currencies = currencies;
        this.currencies$.next(this.currencies);
      },
      error: (error) => {
        console.log(error);
      },
    });
  }
}
